import {
  CTATileData,
  maybeAddCTATile,
} from "@faire/retailer-visitor-shared/components/Carousel/CTATile";
import { ScrollWrapper } from "@faire/retailer-visitor-shared/components/Scroll/ScrollWrapper";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { Spacer } from "@faire/web/slate/spacing";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

interface IProps<T> {
  ctaTileData?: CTATileData;
  /**
   * Defaults to 20
   */
  itemGap?: number;
  itemsPerSlide: number;
  items: ReadonlyArray<T>;
  renderItem: (item: T, index: number) => ReactNode;
  /**
   * This is percentage of the screen and not of the next tile.
   * i.e peekGapPercent={40} will make 40% of the screen for the peeked tile
   * while 60% of the screen will be left available for the rest of the tiles
   * Defaults to 10
   */
  peekGapPercent?: number;
  /**
   * Padding around the carousel in px
   * Defaults to 20
   */
  lateralPadding?: number;
  onScroll?: (start: number, end: number) => void;
  dataTestId?: string;
  onScrollEnd?: () => void;
}

const SCROLL_THROTTLE = 500;

export const Content = styled.div<{
  $itemGap: number;
  $lateralPadding: number;
}>`
  display: flex;
  flex-direction: row;
  ${({ $lateralPadding, $itemGap }) => css`
    padding: 0 ${$lateralPadding}px;
    gap: ${$itemGap}px;
  `}
`;

const ContentItem = styled.div<{
  $itemsPerSlide: number;
  $itemGap: number;
  $peekGapPercent: number;
  $minWidth?: string;
}>`
  ${({ $itemsPerSlide, $itemGap, $peekGapPercent }) => css`
    width: calc(
      ((100vw - ${$peekGapPercent}%) / ${$itemsPerSlide}) - ${$itemGap}px
    );
  `}
  min-width: ${({ $minWidth }) => $minWidth ?? "unset"};
  flex-shrink: 0;
`;

export const HorizontalScrollCarousel = <T,>({
  ctaTileData,
  items: itemsFromProps,
  itemsPerSlide,
  renderItem,
  itemGap = 20,
  peekGapPercent = 10,
  lateralPadding = 20,
  onScroll,
  dataTestId,
  onScrollEnd,
}: IProps<T>) => {
  const items = maybeAddCTATile(itemsFromProps, ctaTileData, itemsPerSlide);
  const { isMobile } = useViewport();
  const [state, setState] = React.useState({ start: 0, end: itemsPerSlide });
  const wrapperRef = React.createRef<HTMLDivElement>();
  const onHorizontalScrollRef = React.useRef<
    ReturnType<typeof debounceIfPossible> | undefined
  >(undefined);

  React.useEffect(() => {
    onHorizontalScrollRef.current = debounceIfPossible(() => {
      const scrollWidth = wrapperRef.current?.scrollWidth;
      const scrollLeft = wrapperRef.current?.scrollLeft;
      if (onScroll && scrollWidth !== undefined && scrollLeft !== undefined) {
        const itemWidthWithGap = scrollWidth / items.length;
        const start = Math.floor(
          (scrollLeft + itemWidthWithGap / 2) / itemWidthWithGap
        );
        const end = start + itemsPerSlide;
        if (start !== state.start && end !== state.end) {
          onScroll(start, end);
          setState({ start, end });
        }
        if (end === items.length) {
          onScrollEnd?.();
        }
      }
    }, SCROLL_THROTTLE);
    return () => {
      if (
        onHorizontalScrollRef.current &&
        "cancel" in onHorizontalScrollRef.current
      ) {
        onHorizontalScrollRef.current?.cancel();
      }
    };
  }, [
    items.length,
    itemsPerSlide,
    onScroll,
    onScrollEnd,
    state.end,
    state.start,
    wrapperRef,
  ]);

  const getTileMinWidth = (item: T | React.ReactElement) => {
    if (
      isMobile &&
      (item as ILayoutElementData)?.icon_text_tile !== undefined
    ) {
      return "232px";
    }
    return undefined;
  };

  const renderContentItem = (item: T | React.ReactElement, index: number) => (
    <ContentItem
      key={index}
      $itemGap={itemGap}
      $itemsPerSlide={itemsPerSlide}
      $peekGapPercent={peekGapPercent}
      $minWidth={getTileMinWidth(item)}
    >
      {React.isValidElement(item) ? item : renderItem(item as T, index)}
    </ContentItem>
  );

  return (
    <ScrollWrapper
      data-test-id={dataTestId}
      $horizontal
      $hideScrollBar
      ref={wrapperRef}
      onScroll={() => onHorizontalScrollRef.current?.()}
    >
      <Content $itemGap={itemGap} $lateralPadding={lateralPadding}>
        {items.map(renderContentItem)}
        <Spacer size={lateralPadding - itemGap} />
      </Content>
    </ScrollWrapper>
  );
};

HorizontalScrollCarousel.Item = ContentItem;
HorizontalScrollCarousel.ItemWrapper = Content;
